import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Person,
  Email,
  Phone,
  Business,
  LocationOn,
  Work,
  Security,
  CheckCircle,
  Key,
  AccountCircle,
  Shield,
  Lock,
  AccountBalance,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import UserService from '../../services/userService';
import useRoleColors from '../../hooks/useRoleColors';
import AuthService from '../../services/authService';

const Profile = ({ user }) => {
  const theme = useTheme();
  const {
    accent,
    accentLight,
    accentDark,
    accentGradient,
    accentGradientHover,
    softBg,
    softBgHover,
    softBgStrong,
    outline,
    contrast,
  } = useRoleColors();
  const [profileData, setProfileData] = useState({
    name: '',
    surname: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    taxNumber: '',
    companyType: '',
    businessArea: '',
    city: '',
    address: '',
    title: '',
    ibanNumber: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '' });

  // Initialize profile data
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        surname: user.surname || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        companyName: user.companyName || '',
        taxNumber: user.taxNumber || '',
        companyType: user.companyType || '',
        businessArea: user.businessArea || '',
        city: user.city || '',
        address: user.address || '',
        title: user.title || '',
        ibanNumber: user.ibanNumber ? formatIbanInput(user.ibanNumber) : '',
      });
    }
  }, [user]);

  function formatIbanInput(value) {
    if (!value) return '';
    const cleanValue = value
      .replace(/\s+/g, '')
      .replace(/[^0-9A-Za-z]/g, '')
      .toUpperCase()
      .slice(0, 26);
    return cleanValue.replace(/(.{4})/g, '$1 ').trim();
  }

  const sanitizeIban = (value) =>
    value ? value.replace(/[^0-9A-Za-z]/g, '').toUpperCase().slice(0, 26) : '';

  // Handle input changes
  const handleInputChange = (field, value) => {
    const nextValue = field === 'ibanNumber' ? formatIbanInput(value) : value;
    setProfileData(prev => ({
      ...prev,
      [field]: nextValue
    }));
    
    if (error) setError('');
    if (success) setSuccess('');
  };

  // Fetch current profile data
  const fetchProfile = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await UserService.getCurrentUserProfile();
      
      if (result.success && result.data) {
        const profileDataFromAPI = result.data.data || result.data;
        setProfileData({
          name: profileDataFromAPI.name || '',
          surname: profileDataFromAPI.surname || '',
          email: profileDataFromAPI.email || '',
          phoneNumber: profileDataFromAPI.phoneNumber || '',
          companyName: profileDataFromAPI.companyName || '',
          taxNumber: profileDataFromAPI.taxNumber || '',
          companyType: profileDataFromAPI.companyType || '',
          businessArea: profileDataFromAPI.businessArea || '',
          city: profileDataFromAPI.city || '',
          address: profileDataFromAPI.address || '',
          title: profileDataFromAPI.title || '',
          ibanNumber: profileDataFromAPI.ibanNumber
            ? formatIbanInput(profileDataFromAPI.ibanNumber)
            : profileDataFromAPI.iban
              ? formatIbanInput(profileDataFromAPI.iban)
              : '',
        });
      } else {
        setError(result.message || 'Profile information could not be retrieved');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Profile information could not be retrieved');
    } finally {
      setLoading(false);
    }
  };

  // Save profile changes
  const saveProfile = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const sanitizedIbanValue = sanitizeIban(profileData.ibanNumber);
      if (sanitizedIbanValue && sanitizedIbanValue.length > 32) {
        const message = 'IBAN number cannot exceed 32 characters.';
        setError(message);
        toast.error(message);
        setSaving(false);
        return;
      }

      const result = await UserService.updateProfile({
        name: profileData.name,
        surname: profileData.surname,
        phoneNumber: profileData.phoneNumber,
        title: profileData.title,
        address: profileData.address,
        ibanNumber: sanitizedIbanValue || null,
      });

      if (result.success) {
        setSuccess(result.message);
        toast.success(result.message);
        setEditMode(false);
        
        const currentUser = AuthService.getCurrentUser() || {};
        const updatedUser = {
          ...currentUser,
          name: profileData.name,
          surname: profileData.surname,
          phoneNumber: profileData.phoneNumber,
          title: profileData.title,
          address: profileData.address,
          ibanNumber: sanitizedIbanValue || '',
        };
        AuthService.updateUserData(updatedUser);
        
        window.dispatchEvent(new CustomEvent('userLogin'));
      } else {
        setError(result.message || 'Profile could not be updated');
        toast.error(result.message || 'Profile could not be updated');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Server error occurred');
      toast.error('Server error occurred');
    } finally {
      setSaving(false);
    }
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (editMode) {
      setConfirmDialog({ open: true, action: 'cancel' });
    } else {
      setEditMode(true);
    }
  };

  // Handle save
  const handleSave = () => {
    setConfirmDialog({ open: true, action: 'save' });
  };

  // Confirm action
  const confirmAction = () => {
    if (confirmDialog.action === 'save') {
      saveProfile();
    } else if (confirmDialog.action === 'cancel') {
      setEditMode(false);
      if (user) {
        setProfileData({
          name: user.name || '',
          surname: user.surname || '',
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
          companyName: user.companyName || '',
          taxNumber: user.taxNumber || '',
          companyType: user.companyType || '',
          businessArea: user.businessArea || '',
          city: user.city || '',
          address: user.address || '',
          title: user.title || '',
          ibanNumber: user.ibanNumber ? formatIbanInput(user.ibanNumber) : '',
        });
      }
    }
    setConfirmDialog({ open: false, action: '' });
  };

  // Close confirm dialog
  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: '' });
  };

  // Initialize profile data on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Profile Card */}
        <Card elevation={8} sx={{ 
          borderRadius: 4,
          overflow: 'hidden',
          mb: 4
        }}>
          
          {/* Profile Header Banner */}
          <Box sx={{ 
            background: accentGradient,
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 80, 
                  height: 80,
                  background: 'rgba(255,255,255,0.25)',
                  fontSize: '2rem',
                  fontWeight: 600,
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  {profileData.name && profileData.surname ?
                    `${profileData.name.charAt(0)}${profileData.surname.charAt(0)}`.toUpperCase() :
                    profileData.name ? profileData.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {profileData.name && profileData.surname ? 
                    `${profileData.name} ${profileData.surname}` : 
                    profileData.name || 'User'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  {profileData.email}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip 
                    label={user?.role || 'User'} 
                    size="small"
                    icon={<Security style={{color:'white'}}/>}
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600,
                      border: '1px solid rgba(255,255,255,0.3)'
                    }}
                  />
                  {profileData.title && (
                    <Chip 
                      label={profileData.title} 
                      size="small"
                      icon={<Work style={{color:'white'}}/>}
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    />
                  )}
                </Stack>
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<Key />}
                    onClick={() => window.location.href = '/change-password'}
                    disabled={loading}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      }
                    }}
                  >
                    Change Password
                  </Button>
                  <Button
                    variant={editMode ? "contained" : "outlined"}
                    startIcon={editMode ? <Cancel /> : <Edit />}
                    onClick={handleEditToggle}
                    disabled={loading}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      backgroundColor: editMode ? 'rgba(244, 67, 54, 0.8)' : 'transparent',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: editMode ? 'rgba(244, 67, 54, 1)' : 'rgba(255,255,255,0.1)',
                      }
                    }}
                  >
                    {editMode ? 'Cancel' : 'Edit Profile'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          {/* Form Content */}
          <CardContent sx={{ p: 4 }}>
            
            {/* Personal Information */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 600,
                color: 'text.primary'
              }}>
                <Person sx={{ mr: 1.5, color: accent }} />
                Personal Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={profileData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    variant="outlined"
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color={editMode ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: accent,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: accent,
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={profileData.surname}
                    onChange={(e) => handleInputChange('surname', e.target.value)}
                    variant="outlined"
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color={editMode ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: accent,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: accent,
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={profileData.email}
                    variant="outlined"
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="disabled" />
                        </InputAdornment>
                      )
                    }}
                    helperText="Email cannot be changed"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'grey.50'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={profileData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    variant="outlined"
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone color={editMode ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: accent,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: accent,
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={profileData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    variant="outlined"
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Work color={editMode ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: accent,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: accent,
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={profileData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    variant="outlined"
                    multiline
                    rows={3}
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                          <LocationOn color={editMode ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: accent,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: accent,
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex' }}>
                  <TextField
                    fullWidth
                    label="IBAN Number"
                    value={profileData.ibanNumber}
                    onChange={(e) => handleInputChange('ibanNumber', e.target.value)}
                    variant="outlined"
                    disabled={!editMode}
                    placeholder="TR44 4444 4444 4444 4444 4444 44"
                    helperText="Supports international IBAN formats."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountBalance color={editMode ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        minWidth: { xs: '100%', sm: '520px', md: '620px' },
                        maxWidth: '100%',
                        '&:hover fieldset': {
                          borderColor: accent,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: accent,
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        fontFamily: "'Roboto Mono', 'Fira Code', monospace",
                        fontSize: '1rem',
                        letterSpacing: '0.12rem',
                        minWidth: { xs: '100%', sm: '480px', md: '560px' }
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Company Information */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 600,
                color: 'text.primary'
              }}>
                <Business sx={{ mr: 1.5, color: accent }} />
                Company Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={profileData.companyName}
                    variant="outlined"
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="disabled" />
                        </InputAdornment>
                      )
                    }}
                    helperText="Contact admin to change"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'grey.50'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tax Number"
                    value={profileData.taxNumber}
                    variant="outlined"
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="disabled" />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'grey.50'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Type"
                    value={profileData.companyType}
                    variant="outlined"
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="disabled" />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'grey.50'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Business Area"
                    value={profileData.businessArea}
                    variant="outlined"
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="disabled" />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'grey.50'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={profileData.city}
                    variant="outlined"
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn color="disabled" />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'grey.50'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Error/Success Messages */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Profile Action Buttons */}
            {editMode && (
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => setConfirmDialog({ open: true, action: 'cancel' })}
                  disabled={saving}
                  startIcon={<Cancel />}
                  sx={{
                    borderColor: accent,
                    color: accent,
                    '&:hover': {
                      borderColor: accentLight,
                      backgroundColor: softBg
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  sx={{ 
                    minWidth: 160,
                    background: accentGradient,
                    boxShadow: `0 4px 15px ${accent}40`,
                    '&:hover': {
                      background: accentGradientHover,
                      boxShadow: `0 6px 20px ${accent}66`,
                    }
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: confirmDialog.action === 'save' ? 'primary.main' : 'error.main'
        }}>
          {confirmDialog.action === 'save' ? <CheckCircle /> : <Cancel />}
          {confirmDialog.action === 'save' ? 'Save Changes' : 'Cancel Changes'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'save' 
              ? 'Are you sure you want to save the changes to your profile?'
              : 'Are you sure you want to cancel the changes? All unsaved changes will be lost.'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirmDialog} variant="outlined">
            No
          </Button>
          <Button 
            onClick={confirmAction} 
            variant="contained"
            autoFocus
            sx={{
              background: confirmDialog.action === 'save' 
                ? accentGradient
                : 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
            }}
          >
            {confirmDialog.action === 'save' ? 'Save' : 'Yes, Cancel Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;